/**
 * TASK-APS-002 & TASK-APS-003: Particle Pool
 *
 * Fixed particle pool for fog and dust particle systems.
 * Creates particles once at initialization to avoid create/destroy overhead during runtime.
 */

import type { ParticleSystemConfig } from '../../types/terrain';

/**
 * Particle data structure
 * Stores all properties needed to render and update a particle
 */
export interface Particle {
  /** Current X position */
  x: number;

  /** Current Y position */
  y: number;

  /** Velocity X (pixels per second) */
  vx: number;

  /** Velocity Y (pixels per second) */
  vy: number;

  /** Particle scale multiplier */
  scale: number;

  /** Current alpha/opacity (0-1) */
  alpha: number;

  /** Base alpha for fade calculations (0-1) */
  baseAlpha: number;
}

/**
 * Creates a fixed pool of particles with randomized initial properties
 *
 * @param config - Particle system configuration
 * @returns Array of initialized particles
 */
export function createParticlePool(config: ParticleSystemConfig): Particle[] {
  const particles: Particle[] = [];

  for (let i = 0; i < config.maxParticles; i++) {
    // Random position within bounds
    const x = config.bounds.x + Math.random() * config.bounds.width;
    const y = config.bounds.y + Math.random() * config.bounds.height;

    // Random speed within range
    const speed = config.speed.min + Math.random() * (config.speed.max - config.speed.min);

    // Random direction within range
    const direction =
      config.direction.min + Math.random() * (config.direction.max - config.direction.min);

    // Convert direction and speed to velocity components
    const vx = Math.cos(direction) * speed;
    const vy = Math.sin(direction) * speed;

    // Random scale within range
    const scale = config.scale.min + Math.random() * (config.scale.max - config.scale.min);

    // Random alpha within range
    const alpha = config.alpha.min + Math.random() * (config.alpha.max - config.alpha.min);

    particles.push({
      x,
      y,
      vx,
      vy,
      scale,
      alpha,
      baseAlpha: alpha, // Store original alpha for fade calculations
    });
  }

  return particles;
}
